<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { OC_STATUS } from '@/constants/OC_STATUS'
import { formatarDataBR } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const colunas = [
  { field: 'numeroOrdem', label: 'Nº Ordem', width: 200, sortable: true, slotName: 'numeroOrdem', class: 'fw-medium text-body-emphasis' },
  { field: 'nomeFornecedor', label: 'Fornecedor', width: 200, sortable: true, slotName: 'nomeFornecedor' },
  { field: 'resumoAprovacao', label: 'Resumo', width: 400, sortable: true, class: 'text-truncate' },
  { field: 'dataEmissao', label: 'Emissão', width: 90, align: 'center', sortable: true, slotName: 'dataEmissao' },
  { field: 'moedaTexto', label: 'Moeda', width: 70, align: 'center', slotName: 'moedaTexto', sortable: true },
  { field: 'valorTotalNegociado', label: 'Valor Total', width: 110, align: 'right', class: 'fw-medium', sortable: true, formatter: (v) => formatarValor(v) },
  { field: 'statusTexto', label: 'Status', width: 90, align: 'center', slotName: 'status', sortable: true },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'nomeFornecedor') {
    return [...data].sort((a, b) => {
      const textoA = (a.nomeFornecedor || '').toLowerCase()
      const textoB = (b.nomeFornecedor || '').toLowerCase()
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'moedaTexto') {
    return [...data].sort((a, b) => {
      const textoA = getMoedaIso(a.lstMoedaPadrao)
      const textoB = getMoedaIso(b.lstMoedaPadrao)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'statusTexto') {
    return [...data].sort((a, b) => {
      const textoA = getStatusTexto(a.lstStatusOrdemCompra)
      const textoB = getStatusTexto(b.lstStatusOrdemCompra)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

const confirmationModal = ref(null)
const itemParaRevisar = ref(null)

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const listas = reactive({
  status: [],
  moedas: []
})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.status = []
    listas.moedas = []
  }
})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['OrdemCompra'])
    listas.status = result.LstStatusOrdemCompra || []
    listas.moedas = result.LstMoedaPadrao || []
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

const getMoedaIso = (idMoeda) => {
  if (!idMoeda) return 'BRL'
  const idStr = String(idMoeda)
  const moeda = listas.moedas.find(m => String(m.idListaClienteItem) === idStr)
  const texto = moeda ? moeda.textoLista : idMoeda
  const match = texto.match(/^[A-Z]{3}/)
  return match ? match[0] : 'BRL'
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const idStr = String(idStatus)
  const item = listas.status.find(s => String(s.idListaClienteItem) === idStr)
  return item ? item.textoLista : idStatus
}

const obterCorStatus = (lstStatus) => {
  if (!lstStatus) return 'badge-ghost-info'
  if (lstStatus === OC_STATUS.Reprovada || lstStatus === OC_STATUS.Cancelada) return 'badge-ghost-danger'
  if (lstStatus === OC_STATUS.Aprovada || lstStatus === OC_STATUS.FaturadaParcialmente || lstStatus === OC_STATUS.Encerrada || lstStatus === OC_STATUS.EncerradaParcialmente) return 'badge-ghost-success'
  if (lstStatus === OC_STATUS.Rascunho) return 'badge-ghost-warning'
  return 'badge-ghost-info'
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)

    const response = await api.get(`/OrdensCompra?${params.toString()}`)
    lista.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar as ordens de compra.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.busca = ''
  filtros.lstStatus = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarRegistro = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/OrdensCompra/${item.idOrdemCompra}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da ordem de compra.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idOrdemCompra
  auditoriaSelecionada.nome = `Ordem ${item.numeroOrdem || item.idOrdemCompra}`
  mostrarAuditoria.value = true
}

const revisarOrdemCompra = (item) => {
  itemParaRevisar.value = item
  confirmationModal.value?.show()
}

const handleConfirmacaoRevisao = async (confirmado) => {
  if (!confirmado || !itemParaRevisar.value) {
    itemParaRevisar.value = null
    return
  }

  const item = itemParaRevisar.value
  itemParaRevisar.value = null

  try {
    const response = await api.post(`/OrdensCompra/${item.idOrdemCompra}/revisar`)
    const novaOC = response.data
    emit('show-notification', { message: `Revisão criada: ${novaOC.numeroOrdem}`, type: 'success' })
    emit('editar-item', novaOC)
    buscarDados(paginacao.value.CurrentPage || 1)
  } catch (error) {
    const msg = error.response?.data?.message || error.response?.data || 'Erro ao criar revisão'
    emit('show-notification', { message: typeof msg === 'string' ? msg : 'Erro ao criar revisão', type: 'error' })
  }
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Ordens de Compra"
    subtitulo="Gestão de Ordens de Compra"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    label-botao-adicionar="Nova Ordem de Compra"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Buscar (Nº Ordem)</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3 ms-auto text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma ordem de compra encontrada."
      row-key="idOrdemCompra"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegistro($event.row)"
    >
      <template #numeroOrdem="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.numeroOrdem }}</span>
      </template>

      <template #nomeFornecedor="{ row }">
        <span class="text-truncate d-inline-block" style="max-width: 250px;" :title="row.nomeFornecedor">{{ row.nomeFornecedor }}</span>
      </template>

      <template #dataEmissao="{ row }">
        <span class="small">{{ formatarDataBR(row.dataEmissao) }}</span>
      </template>

      <template #moedaTexto="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getMoedaIso(row.lstMoedaPadrao) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusOrdemCompra)">
          {{ getStatusTexto(row.lstStatusOrdemCompra) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button
            v-if="row.lstStatusOrdemCompra === 'OC_STATUS6'"
            class="btn-icon text-warning"
            @click="revisarOrdemCompra(row)"
            title="Cria uma cópia desta Ordem de Compra no status Rascunho para correção e reenvio para aprovação. A OC original permanece no status Reprovada para auditoria."
          >
            <i class="bi bi-file-earmark-plus"></i>
          </button>
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <ConfirmationModal
    ref="confirmationModal"
    title="Copiar OC Reprovada"
    :message="`Deseja criar uma cópia da OC <strong>${itemParaRevisar?.numeroOrdem ?? ''}</strong> no status Rascunho para correção e reenvio para aprovação?`"
    confirm-button-text="Sim, Criar Cópia"
    cancel-button-text="Cancelar"
    @confirmed="handleConfirmacaoRevisao"
  />

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="OrdemCompra"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>
